import { css } from '@emotion/react';
import { Avatar, Box, Divider, ListItem, Menu, MenuItem, Stack, styled, Typography, useTheme } from '@mui/material';
import { UserCircleIcon } from '@phosphor-icons/react';
import type { MouseEvent } from 'react';
import { Fragment, useState } from 'react';

import Logo from '@/assets/logos/parken-in-hessen.svg?react';
import { SectionWrapper } from '@/components/SectionWrapper';
import { MobileMenu } from '@/components/navigations/MobileMenu';
import { StyledLink } from '@/components/navigations/StyledLink';
import { StyledList } from '@/components/navigations/StyledList';
import { MenuItemLink } from '@/components/router/MenuItemLink';
import { useLogout } from '@/hooks/useLogout';
import { useOptionalUserQuery } from '@/hooks/useOptionalUserQuery';
import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';
import { SCREEN_HEIGHT_BREAKPOINT } from '@/modules/SCREEN_HEIGHT_BREAKPOINT';
import { useManagementLinks } from '@/utils/navigationLinks';

const Root = styled(SectionWrapper)(
  ({ theme }) => css`
    @media (min-height: ${SCREEN_HEIGHT_BREAKPOINT}) and (min-width: ${theme.breakpoints.values[MAIN_BREAKPOINT]}px) {
      position: sticky;
      top: 0;
      z-index: 1300;
      height: ${theme.spacing(8)};
      background: ${theme.palette.background.paper};
    }
  `,
);

const MobileMenuWrapper = styled(Box)(
  ({ theme }) => css`
    display: flex;
    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      display: none;
    }
  `,
);

const StyledListWrapper = styled(Box)(
  ({ theme }) => css`
    display: none;

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      display: flex;
    }
  `,
);

export function Header() {
  const { logout } = useLogout();
  const { data: user } = useOptionalUserQuery();

  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const links = useManagementLinks();

  return (
    <Root as="header">
      <Stack direction="row" gap={2}>
        <StyledLink sx={{ display: 'flex', alignItems: 'center' }} to="/">
          <Logo style={{ maxHeight: theme.spacing(6), height: 'auto' }} width={128} />
        </StyledLink>

        <StyledListWrapper>
          {user ? (
            <StyledList role="menubar">
              {links.map(({ title, link }) => (
                <ListItem key={title} role="none" sx={{ p: 0 }}>
                  <StyledLink {...link} role="menuitem">
                    {title}
                  </StyledLink>
                </ListItem>
              ))}
            </StyledList>
          ) : null}
        </StyledListWrapper>
      </Stack>
      <Stack direction="row" gap={2}>
        {user ? (
          <Fragment>
            <Avatar
              onClick={handleClick}
              sx={{
                bgcolor: theme.palette.common.black,
                color: theme.palette.common.white,
                cursor: 'pointer',
              }}
            >
              <UserCircleIcon size={24} weight="fill" />
            </Avatar>

            <Menu
              anchorEl={anchorEl}
              onClose={handleClose}
              open={open}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Stack alignItems="center" px={2} py={1.5}>
                <Typography variant="body1">
                  {user.firstName} {user.lastName}
                </Typography>
              </Stack>
              <Divider />

              <MenuItemLink onClick={handleClose} to="/management/profile">
                Profil bearbeiten
              </MenuItemLink>
              <MenuItem
                onClick={() => {
                  handleClose();
                  logout();
                }}
              >
                Logout
              </MenuItem>
            </Menu>
          </Fragment>
        ) : null}
        <MobileMenuWrapper>
          <MobileMenu />
        </MobileMenuWrapper>
      </Stack>
    </Root>
  );
}
